import { useState } from 'react'
import { Link } from 'react-router-dom'
import { useChainVerify, useLatestCheckpoint } from '../hooks/useContacts'
import { usePageMeta } from '../hooks/usePageMeta'

const OTS_STATUS_LABELS: Record<string, string> = {
  none: 'Not yet anchored',
  pending: 'Anchoring to Bitcoin — awaiting confirmation',
  confirmed: 'Anchored to Bitcoin',
}

const BREAK_REASON_EXPLANATIONS: Record<string, string> = {
  missing_sequence: 'An entry appears to be missing from the sequence — a record may have been deleted.',
  hash_mismatch: 'The link between two entries doesn\'t match — the sequence may have been reordered or spliced.',
  recomputation_mismatch: 'An entry\'s recorded fingerprint doesn\'t match its own content — it may have been edited after being written.',
  tip_mismatch: 'The ledger\'s latest known state doesn\'t match what verification found.',
}

export default function LedgerVerifyPage() {
  usePageMeta(
    'Ledger Verification',
    'Independently verify the tamper-evident, cryptographically chained ledger recording every report submission and contact status change on Biosecurity Reporter.',
    '/ledger/verify',
  )

  const { data, isLoading, isError } = useChainVerify()
  const { data: checkpoint } = useLatestCheckpoint()
  const [showTechnical, setShowTechnical] = useState(false)

  return (
    <div className="min-h-screen bg-gray-50 px-4 py-10">
      <div className="mx-auto max-w-2xl">
        <Link to="/" className="text-sm text-blue-600 hover:text-blue-700 font-medium">
          ← Back to map
        </Link>

        <div className="mt-4 mb-8">
          <h1 className="text-2xl font-bold text-gray-900 tracking-tight">Ledger Verification</h1>
          <p className="mt-1 text-sm text-gray-500">
            Every report and contact status change on this platform is recorded on a
            tamper-evident, cryptographically chained ledger — each entry is linked to the
            one before it, so nothing can be silently added, altered, or removed without
            breaking the chain. This page independently re-checks the entire history right
            now, live, against its own recorded fingerprints.
          </p>
        </div>

        {isLoading && (
          <div className="rounded-xl border border-gray-200 bg-white px-5 py-4 text-sm text-gray-500">
            Checking the ledger…
          </div>
        )}

        {isError && (
          <div className="rounded-xl border-2 border-amber-300 bg-amber-50 px-5 py-4">
            <p className="text-sm font-semibold text-amber-800">Couldn't reach the ledger right now.</p>
            <p className="text-sm text-amber-700 mt-1">
              This is most likely a temporary connection issue, not a sign of a problem with
              the ledger itself. Try reloading in a moment.
            </p>
          </div>
        )}

        {data && data.valid && (
          <div className="rounded-xl border-2 border-green-300 bg-green-50 px-5 py-4">
            <p className="text-sm font-semibold text-green-800 flex items-center gap-2">
              <span className="inline-flex h-5 w-5 items-center justify-center rounded-full bg-green-600 text-white text-xs">✓</span>
              Verified — no tampering detected
            </p>
            <p className="text-sm text-green-700 mt-2">
              All <strong>{data.verified_count.toLocaleString()}</strong> entries in the ledger
              were independently recomputed and checked, and every single one links correctly
              to the one before it, all the way back to the start.
            </p>
          </div>
        )}

        {data && !data.valid && (
          <div className="rounded-xl border-2 border-red-300 bg-red-50 px-5 py-4">
            <p className="text-sm font-semibold text-red-800 flex items-center gap-2">
              <span className="inline-flex h-5 w-5 items-center justify-center rounded-full bg-red-600 text-white text-xs">✕</span>
              Verification failed
            </p>
            <p className="text-sm text-red-700 mt-2">
              {data.break_reason && BREAK_REASON_EXPLANATIONS[data.break_reason]
                ? BREAK_REASON_EXPLANATIONS[data.break_reason]
                : 'The ledger did not verify cleanly.'}
              {data.break_sequence !== null && (
                <> The break was found at entry #{data.break_sequence}.</>
              )}
            </p>
            <p className="text-sm text-red-700 mt-2">
              {data.verified_count.toLocaleString()} entries before that point verified
              correctly. If you believe this is a genuine integrity issue, please contact{' '}
              <a href="mailto:robrussell.development@gmail.com" className="underline font-medium">
                robrussell.development@gmail.com
              </a>.
            </p>
          </div>
        )}

        {checkpoint && (
          <div className="mt-6 rounded-xl border border-gray-200 bg-white px-5 py-4">
            <h2 className="text-sm font-semibold text-gray-900">Latest chain-tip checkpoint</h2>
            <p className="mt-1 text-sm text-gray-600">
              A checkpoint is a signed snapshot of the ledger's tip taken at a point in time,
              signed with a key held by a separate service — not this platform's own database —
              so that a compromise here alone can't forge a backdated checkpoint. This closes
              a gap the chain check above can't: entries deleted from the very end would still
              replay as "valid" above, but wouldn't match the last checkpoint's recorded state.
            </p>
            <div className="mt-3 space-y-1.5 text-sm font-mono">
              <div className="flex justify-between gap-4">
                <span className="text-gray-500">checkpoint sequence</span>
                <span className="text-gray-900">#{checkpoint.chain_sequence}</span>
              </div>
              <div className="flex justify-between gap-4">
                <span className="text-gray-500">taken at</span>
                <span className="text-gray-900">
                  {new Date(checkpoint.created_at).toLocaleString()}
                </span>
              </div>
              <div className="flex justify-between gap-4">
                <span className="text-gray-500">Bitcoin anchor</span>
                <span className="text-gray-900">
                  {OTS_STATUS_LABELS[checkpoint.ots_status] ?? checkpoint.ots_status}
                  {checkpoint.ots_bitcoin_block_height != null &&
                    ` (block ${checkpoint.ots_bitcoin_block_height.toLocaleString()})`}
                </span>
              </div>
            </div>
          </div>
        )}

        {data && (
          <div className="mt-4">
            <button
              type="button"
              onClick={() => setShowTechnical((v) => !v)}
              className="text-xs text-gray-500 hover:text-gray-700 font-medium"
            >
              {showTechnical ? '▾' : '▸'} Technical details
            </button>
            {showTechnical && (
              <div className="mt-2 rounded-lg border border-gray-200 bg-white p-3 space-y-1.5 text-sm font-mono">
                <div className="flex justify-between gap-4">
                  <span className="text-gray-500">valid</span>
                  <span className="text-gray-900">{String(data.valid)}</span>
                </div>
                <div className="flex justify-between gap-4">
                  <span className="text-gray-500">verified_count</span>
                  <span className="text-gray-900">{data.verified_count}</span>
                </div>
                <div className="flex justify-between gap-4">
                  <span className="text-gray-500">tip_sequence</span>
                  <span className="text-gray-900">{data.tip_sequence}</span>
                </div>
                <div className="flex justify-between gap-4 items-start">
                  <span className="text-gray-500 shrink-0">tip_hash</span>
                  <span className="text-gray-900 text-xs break-all text-right">{data.tip_hash ?? '—'}</span>
                </div>
                <div className="flex justify-between gap-4">
                  <span className="text-gray-500">break_sequence</span>
                  <span className="text-gray-900">{data.break_sequence ?? 'null'}</span>
                </div>
                <div className="flex justify-between gap-4">
                  <span className="text-gray-500">break_reason</span>
                  <span className="text-gray-900">{data.break_reason ?? 'null'}</span>
                </div>
              </div>
            )}
          </div>
        )}

        <div className="mt-10">
          <Link to="/" className="text-sm text-blue-600 hover:text-blue-700 font-medium">
            ← Back to map
          </Link>
        </div>
      </div>
    </div>
  )
}
